/**
 * Smart Prefetching System
 * Intelligently preloads data for anticipated navigation
 * Optimizes for perceived performance without wasting resources
 */

import { getCacheManager } from './cache-manager';

class PrefetchManager {
  constructor(apiClient) {
    this.apiClient = apiClient;
    this.cacheManager = getCacheManager();
    this.prefetchQueue = new Set();
    this.prefetchInProgress = new Map();
    this.routePrefetchMap = new Map(); // Maps routes to prefetch endpoints
    this.setupRouteMap();
  }

  /**
   * Setup which endpoints to prefetch for each route
   */
  setupRouteMap() {
    // Main dashboard routes
    this.routePrefetchMap.set('/dashboard', [
      { url: '/dashboard', priority: 1 },
      { url: '/notifications', priority: 2 },
      { url: '/interview/history', priority: 3 },
    ]);

    this.routePrefetchMap.set('/interviews', [
      { url: '/interview/history', priority: 1 },
      { url: '/interview-types', priority: 2 },
    ]);

    this.routePrefetchMap.set('/interview/live', [
      { url: '/interview/live', priority: 1 },
      { url: '/interview/stream', priority: 2 },
    ]);

    this.routePrefetchMap.set('/profile', [
      { url: '/user/profile', priority: 1 },
      { url: '/user/settings', priority: 2 },
    ]);

    this.routePrefetchMap.set('/reports', [
      { url: '/reports', priority: 1 },
      { url: '/analytics', priority: 2 },
    ]);
  }

  /**
   * Prefetch data for a route on hover/intent
   */
  prefetchForRoute(route) {
    const endpoints = this.routePrefetchMap.get(route);
    if (!endpoints) {
      return;
    }

    // Sort by priority and prefetch
    endpoints
      .sort((a, b) => a.priority - b.priority)
      .forEach((endpoint) => {
        this.prefetch(endpoint.url);
      });
  }

  /**
   * Prefetch a single endpoint
   */
  async prefetch(url, params = {}) {
    const key = `${url}?${JSON.stringify(params)}`;

    // Skip if already prefetching
    if (this.prefetchInProgress.has(key)) {
      return this.prefetchInProgress.get(key);
    }

    // Skip if already cached
    const cached = await this.cacheManager.get(url, params);
    if (cached.data && !this.cacheManager.isStale(url, params)) {
      return Promise.resolve();
    }

    // Execute prefetch
    const promise = this.executePrefetch(url, params, key);
    this.prefetchInProgress.set(key, promise);

    return promise;
  }

  /**
   * Execute prefetch with low priority
   */
  async executePrefetch(url, params, key) {
    try {
      // Use requestIdleCallback for low-priority prefetch
      if ('requestIdleCallback' in window) {
        await new Promise((resolve) => {
          requestIdleCallback(() => {
            this.apiClient.get(url, { params }).catch((error) => {
              console.debug('Prefetch failed (non-critical):', url, error.message);
            });
            resolve();
          });
        });
      } else {
        // Fallback: prefetch after a delay
        await new Promise((resolve) => {
          setTimeout(() => {
            this.apiClient.get(url, { params }).catch((error) => {
              console.debug('Prefetch failed (non-critical):', url, error.message);
            });
            resolve();
          }, 1000);
        });
      }
    } catch (error) {
      console.debug('Prefetch error:', error);
    } finally {
      this.prefetchInProgress.delete(key);
    }
  }

  /**
   * Setup route transition prefetching
   * Call in route guard or link component
   */
  setupRoutePrefetch(toRoute, fromRoute) {
    // Prefetch data for destination route
    this.prefetchForRoute(toRoute);

    // Optionally prefetch sibling routes
    const siblings = this.getSiblingRoutes(toRoute);
    siblings.forEach((route) => {
      // Low priority - prefetch after main route data
      setTimeout(() => this.prefetchForRoute(route), 500);
    });
  }

  /**
   * Get sibling routes for intelligent prefetch
   */
  getSiblingRoutes(currentRoute) {
    const routes = Array.from(this.routePrefetchMap.keys());
    const baseRoute = currentRoute.split('/').slice(0, -1).join('/');

    return routes.filter(
      (route) =>
        route.startsWith(baseRoute) && route !== currentRoute,
    );
  }

  /**
   * Setup link hover detection for prefetch
   */
  setupLinkHoverPrefetch() {
    if (typeof document === 'undefined') {
      return;
    }

    document.addEventListener('mouseover', (e) => {
      const link = e.target.closest('a[data-prefetch]');
      if (!link) return;

      const route = link.getAttribute('href');
      if (route) {
        this.prefetchForRoute(route);
      }
    });
  }

  /**
   * Setup connection quality detection for adaptive prefetch
   */
  setupConnectionAwarePrefetch() {
    if (!('connection' in navigator)) {
      return;
    }

    const connection = navigator.connection;

    connection.addEventListener('change', () => {
      const effectiveType = connection.effectiveType;

      // Only aggressive prefetch on good connections
      if (effectiveType === '4g') {
        // Can prefetch more aggressively
        console.log('Good connection detected - enabling aggressive prefetch');
      } else if (effectiveType === 'slow-2g' || effectiveType === '2g') {
        // Minimal prefetch
        console.log('Slow connection - reducing prefetch activity');
      }
    });
  }

  /**
   * Cancel all pending prefetches
   */
  cancelPrefetch() {
    this.prefetchInProgress.clear();
    this.prefetchQueue.clear();
  }

  /**
   * Get prefetch stats
   */
  getStats() {
    return {
      queued: this.prefetchQueue.size,
      inProgress: this.prefetchInProgress.size,
      routesRegistered: this.routePrefetchMap.size,
    };
  }
}

export default PrefetchManager;

/**
 * React Hook for link hover prefetch
 * Usage: <Link to="/route" onMouseEnter={() => usePrefetch('/route')} />
 */
export const usePrefetch = (apiClient) => {
  const prefetchManager = new PrefetchManager(apiClient);

  return {
    prefetchForRoute: (route) => prefetchManager.prefetchForRoute(route),
    prefetchEndpoint: (url, params) => prefetchManager.prefetch(url, params),
    prefetchStats: () => prefetchManager.getStats(),
  };
};
